import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import { CoverageType, PerilType } from './utility';
import { ComparePerils } from './ComparePerils';

interface ComparePropertyCoveragesProps {
  comparisonPropertyByLocation: Map<string, Array<CoverageType>>;
  policyPropertyByLocation: Map<string, Array<CoverageType>>;
}

export const ComparePropertyCoverages = ({
  comparisonPropertyByLocation,
  policyPropertyByLocation,
}: ComparePropertyCoveragesProps) => {
  const [sortedLocationKeys, setSortedLocationKeys] = useState<string[]>([]);

  useEffect(() => {
    // Get all unique location keys from both maps
    const allLocationKeys = new Set<string>([
      ...comparisonPropertyByLocation.keys(),
      ...policyPropertyByLocation.keys(),
    ]);

    // Convert to sorted array for consistent rendering
    const sorted = Array.from(allLocationKeys).sort();
    setSortedLocationKeys(sorted);
  }, [comparisonPropertyByLocation, policyPropertyByLocation]);

  return (
    <>
      {sortedLocationKeys.map((locationKey) => (
        <CompareCoverages
          key={locationKey}
          label={locationKey}
          quoteCoverages={policyPropertyByLocation.get(locationKey) || []}
          comparisonCoverages={
            comparisonPropertyByLocation.get(locationKey) || []
          }
          locationAddress={locationKey}
        />
      ))}
    </>
  );
};

interface CompareCoveragesProps {
  label: string;
  quoteCoverages: Array<CoverageType>;
  comparisonCoverages: Array<CoverageType>;
  locationAddress?: string;
}

export const CompareCoverages = ({
  label,
  quoteCoverages,
  comparisonCoverages,
  locationAddress,
}: CompareCoveragesProps) => {
  const [perilMap, setPerilMap] = useState<
    Map<
      string,
      { quotePeril: PerilType | null; comparisonPeril: PerilType | null }
    >
  >(new Map());

  useEffect(() => {
    // Collect all perils from quote coverages
    const quotePerils = quoteCoverages.flatMap(
      (coverage) => coverage.perils || [],
    );

    // Collect all perils from comparison coverages
    const comparisonPerils = comparisonCoverages.flatMap(
      (coverage) => coverage.perils || [],
    );

    // Get all unique peril descriptions
    const allPerilDescriptions = new Set<string>([
      ...quotePerils.map((peril) => peril.name),
      ...comparisonPerils.map((peril) => peril.name),
    ]);

    // Create a map between peril descriptions and {quotePeril, comparisonPeril}
    const newPerilMap = Array.from(allPerilDescriptions).reduce(
      (map, description) => {
        const quotePeril =
          quotePerils.find((peril) => peril.name === description) || null;
        const comparisonPeril =
          comparisonPerils.find((peril) => peril.name === description) || null;
        map.set(description, { quotePeril, comparisonPeril });
        return map;
      },
      new Map<
        string,
        { quotePeril: PerilType | null; comparisonPeril: PerilType | null }
      >(),
    );

    setPerilMap(newPerilMap);
  }, [quoteCoverages, comparisonCoverages, locationAddress]);

  return (
    <CoveragesComparisonContainer>
      <CoverageLabelTypography variant='subtitle2'>
        {label}
      </CoverageLabelTypography>
      {Array.from(perilMap.entries()).map(
        ([description, { quotePeril, comparisonPeril }]) => (
          <PerilItemContainer key={description}>
            <PerilDescriptionTypography variant='subtitle2'>
              {description}
            </PerilDescriptionTypography>
            <ComparePerils
              quotePeril={quotePeril}
              comparisonPeril={comparisonPeril}
            />
          </PerilItemContainer>
        ),
      )}
    </CoveragesComparisonContainer>
  );
};

interface CompareAdditionalCoveragesProps {
  quotePerils: Array<PerilType>;
  comparisonPerils: Array<PerilType>;
}

export const CompareAdditionalCoverages = ({
  quotePerils,
  comparisonPerils,
}: CompareAdditionalCoveragesProps) => {
  const [perilMap, setPerilMap] = useState<
    Map<
      string,
      { quotePeril: PerilType | null; comparisonPeril: PerilType | null }
    >
  >(new Map());

  useEffect(() => {
    // Get all unique peril names (already modified for Equipment Breakdown)
    const allPerilNames = new Set<string>([
      ...quotePerils.map((peril) => peril.name),
      ...comparisonPerils.map((peril) => peril.name),
    ]);

    // Create a map between peril names and {quotePeril, comparisonPeril}
    const newPerilMap = Array.from(allPerilNames).reduce((map, name) => {
      const quotePeril =
        quotePerils.find((peril) => peril.name === name) || null;
      const comparisonPeril =
        comparisonPerils.find((peril) => peril.name === name) || null;
      map.set(name, { quotePeril, comparisonPeril });
      return map;
    }, new Map<string, { quotePeril: PerilType | null; comparisonPeril: PerilType | null }>());

    setPerilMap(newPerilMap);
  }, [quotePerils, comparisonPerils]);

  return (
    <CoveragesComparisonContainer>
      <CoverageLabelTypography variant='subtitle2'>
        Additional Coverages
      </CoverageLabelTypography>
      {Array.from(perilMap.entries()).map(
        ([name, { quotePeril, comparisonPeril }]) => (
          <PerilItemContainer key={name}>
            <PerilDescriptionTypography variant='subtitle2'>
              {name}
            </PerilDescriptionTypography>
            <ComparePerils
              quotePeril={quotePeril}
              comparisonPeril={comparisonPeril}
              hideRate={true}
            />
          </PerilItemContainer>
        ),
      )}
    </CoveragesComparisonContainer>
  );
};

const CoveragesComparisonContainer = styled(Box)({});

const CoverageLabelTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: theme.typography.h6.fontSize,
  marginBottom: theme.spacing(2),
}));

const PerilItemContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const PerilDescriptionTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));
